// @flow
import { type ActionType, createAction, handleActions } from 'redux-actions';
import produce from 'immer';
import { applyPenders } from 'redux-pender';
import * as ProductsAPI from 'lib/api/products';

const GET_RECENT_PRODUCTS = 'display/GET_RECENT_PRODUCTS';
const PREFETCH_RECENT_PRODUCTS = 'display/PREFETCH';
const REVEAL_PREFETCHED = 'display/REVEAL_PREFETCHED';
const CLEAR_USER_PRODUCTS = 'display/CLEAR_USER_PRODUCTS';
const GET_USER_PRODUCTS = 'display/GET_USER_PRODUCTS';
const PREFETCH_USER_PRODUCTS = 'display/PREFETCH_USER_PRODUCTS';
const GET_TRENDING_PRODUCTS = 'display/GET_TRENDING_PRODUCTS';
const PREFETCH_TRENDING_PRODUCTS = 'display/PREFETCH_TRENDING_PRODUCTS';
const GET_TAG_PRODUCTS = 'display/GET_TAG_PRODUCTS';
const PREFETCH_TAG_PRODUCTS = 'display/PREFETCH_TAG_PRODUCTS';
const GET_BADGE_PRODUCTS = 'display/GET_BADGE_PRODUCTS';
const PREFETCH_BADGE_PRODUCTS = 'display/PREFETCH_BADGE_PRODUCTS';
const GET_CATEGORY_PRODUCTS = 'display/GET_CATEGORY_PRODUCTS';
const PREFETCH_CATEGORY_PRODUCTS = 'display/PREFETCH_CATEGORY_PRODUCTS';
const CLEAR_CATEGORY_PRODUCTS = 'display/CLEAR_CATEGORY_PRODUCTS';
const CLEAR_TAG_PRODUCTS = 'display/CLEAR_TAG_PRODUCTS';
const CLEAR_BADGE_PRODUCTS = 'display/CLEAR_BADGE_PRODUCTS';
const GET_TEMP_PRODUCTS = 'display/GET_TEMP_PRODUCTS';
const PREFETCH_TEMP_PRODUCTS = 'display/PREFETCH_TEMP_PRODUCTS';
const REMOVE_TEMP_PRODUCT = 'display/REMOVE_TEMP_PRODUCT';
const FLUSH_LIST = 'display/FLUSH_LIST';

// potential improvement :: remove duplicates
export const actionCreators = {
  getRecentProducts: createAction(GET_RECENT_PRODUCTS, ProductsAPI.getPublicProducts),
  prefetchRecentProducts: createAction(PREFETCH_RECENT_PRODUCTS, ProductsAPI.getPublicProducts),
  revealPrefetched: createAction(REVEAL_PREFETCHED, (type: string) => type),
  clearUserProducts: createAction(CLEAR_USER_PRODUCTS),
  getUserProducts: createAction(
    GET_USER_PRODUCTS,
    ProductsAPI.getUserProducts,
    (meta: { username: string, tag?: string, badge?: string }) => meta,
  ),
  prefetchUserProducts: createAction(PREFETCH_USER_PRODUCTS, ProductsAPI.getUserProducts),
  getTrendingProducts: createAction(GET_TRENDING_PRODUCTS, ProductsAPI.getTrendingProducts),
  prefetchTrendingProducts: createAction(
    PREFETCH_TRENDING_PRODUCTS,
    ProductsAPI.getTrendingProducts,
  ),
  getTagProducts: createAction(
    GET_TAG_PRODUCTS,
    ProductsAPI.getPublicProductsByTag,
    (meta: { tag: string }) => meta,
  ),
  prefetchTagProducts: createAction(PREFETCH_TAG_PRODUCTS, ProductsAPI.getPublicProductsByTag),
  getBadgeProducts: createAction(
    GET_BADGE_PRODUCTS,
    ProductsAPI.getPublicProductsByBadge,
    (meta: { badge: string }) => meta,
  ),
  prefetchBadgeProducts: createAction(
    PREFETCH_BADGE_PRODUCTS,
    ProductsAPI.getPublicProductsByBadge,
  ),
  getCategoryProducts: createAction(
    GET_CATEGORY_PRODUCTS,
    ProductsAPI.getPublicProductsByCategory,
    (meta: { category: string, sort?: string }) => meta,
  ),
  prefetchCategoryProducts: createAction(
    PREFETCH_CATEGORY_PRODUCTS,
    ProductsAPI.getPublicProductsByCategory,
  ),
  clearCategoryProducts: createAction(CLEAR_CATEGORY_PRODUCTS),
  clearTagProducts: createAction(CLEAR_TAG_PRODUCTS),
  clearBadgeProducts: createAction(CLEAR_BADGE_PRODUCTS),
  getTempProducts: createAction(GET_TEMP_PRODUCTS, ProductsAPI.getTempProducts),
  prefetchTempProducts: createAction(PREFETCH_TEMP_PRODUCTS, ProductsAPI.getTempProducts),
  removeTempProduct: createAction(REMOVE_TEMP_PRODUCT, (productId: string) => productId),
  flushList: createAction(FLUSH_LIST),
};

export type ProductItem = {
  id: string,
  title: string,
  body: string,
  thumbnail: string,
  is_markdown: boolean,
  is_private: boolean,
  released_at: string,
  created_at: string,
  updated_at: string,
  tags: string[],
  badges: string[],
  categories: any[],
  url_slug: string,
  likes: number,
  comments_count: number,
  user: {
    id: string,
    username: string,
    display_name: string,
    short_bio: string,
    thumbnail: ?string,
  },
  meta: any,
};

export type DisplaySet = {
  products: ?(ProductItem[]),
  prefetched: ?(ProductItem[]),
  end: boolean,
};

export type Display = {
  trendingMap: {
    [id: string]: boolean,
  },
  trending: DisplaySet,
  recent: DisplaySet,
  user: DisplaySet & { currentUsername: ?string, currentTag: ?string, currentBadge: ?string },
  tag: DisplaySet & { currentTag: ?string },
  badge: DisplaySet & { currentBadge: ?string },
  category: DisplaySet & { currentCategory: ?string },
  temp: DisplaySet,
};

type RevealPrefetchedAction = ActionType<typeof actionCreators.revealPrefetched>;
type ProductsResponseAction = {
  type: string,
  payload: {
    data: ProductItem[],
    category?: ?string,
    sort?: ?string,
  },
  meta: any,
};
type RemoveTempProductAction = ActionType<typeof actionCreators.removeTempProduct>;

export interface DisplayActionCreators {
  getRecentProducts(cursor: string, category: string, sort: string): any;

  prefetchRecentProducts(cursor: string, category: string, sort: string): any;

  revealPrefetched(type: string): any;

  getUserProducts(payload: ProductsAPI.GetUserProductsPayload): any;

  prefetchUserProducts(payload: ProductsAPI.GetUserProductsPayload): any;

  getTrendingProducts(): any;

  prefetchTrendingProducts(offset: number): any;

  getTagProducts(payload: ProductsAPI.GetPublicProductsByTagPayload): any;

  prefetchTagProducts(payload: ProductsAPI.GetPublicProductsByTagPayload): any;

  getBadgeProducts(payload: ProductsAPI.GetPublicProductsByBadgePayload): any;

  prefetchBadgeProducts(payload: ProductsAPI.GetPublicProductsByBadgePayload): any;

  getCategoryProducts(payload: ProductsAPI.GetPublicProductsByCategoryPayload): any;

  prefetchCategoryProducts(payload: ProductsAPI.GetPublicProductsByCategoryPayload): any;

  getTempProducts(payload: ProductsAPI.GetTempProductsPayload): any;

  prefetchTempProducts(payload: ProductsAPI.GetTempProductsPayload): any;

  clearCategoryProducts(): any;

  clearTagProducts(): any;

  clearBadgeProducts(): any;

  clearUserProducts(): any;

  removeTempProduct(productId: string): any;

  flushList(): any;
}

const initialDisplaySet = {
  products: null,
  prefetched: null,
  end: false,
};

const initialState: Display = {
  trendingMap: {},
  trending: initialDisplaySet,
  recent: initialDisplaySet,
  user: { ...initialDisplaySet, currentUsername: null, currentTag: null, currentBadge: null },
  tag: { ...initialDisplaySet, currentTag: null },
  badge: { ...initialDisplaySet, currentBadge: null },
  category: { ...initialDisplaySet, currentCategory: null },
  temp: initialDisplaySet,
};

const reducer = handleActions(
  {
    [REVEAL_PREFETCHED]: (state, action: RevealPrefetchedAction) => {
      return produce(state, draft => {
        const { payload } = action;
        const { products, prefetched } = draft[payload];
        if (products && prefetched) {
          products.push(...prefetched);
          draft[payload].prefetched = null;
        }
      });
    },
    [CLEAR_USER_PRODUCTS]: state => {
      return produce(state, draft => {
        draft.user = {
          products: null,
          prefetched: null,
          end: false,
          currentUsername: null,
          currentTag: null,
          currentBadge: null,
        };
      });
    },
    [CLEAR_TAG_PRODUCTS]: state => {
      return produce(state, draft => {
        draft.tag = {
          products: null,
          prefetched: null,
          end: false,
          currentTag: null,
        };
      });
    },
    [CLEAR_BADGE_PRODUCTS]: state => {
      return produce(state, draft => {
        draft.badge = {
          products: null,
          prefetched: null,
          end: false,
          currentBadge: null,
        };
      });
    },
    [CLEAR_CATEGORY_PRODUCTS]: state => {
      return produce(state, draft => {
        draft.category = {
          products: null,
          prefetched: null,
          end: false,
          currentCategory: null,
        };
      });
    },
    [REMOVE_TEMP_PRODUCT]: (state, { payload }: RemoveTempProductAction) => {
      return produce(state, draft => {
        if (!draft.temp.products) return;
        draft.temp.products = draft.temp.products.filter(p => p.id !== payload);
      });
    },
    [FLUSH_LIST]: state => {
      return initialState;
    },
  },
  initialState,
);

export default applyPenders(reducer, [
  {
    type: GET_RECENT_PRODUCTS,
    onSuccess: (state: Display, action: ProductsResponseAction) => {
      const { category, sort } = action.payload;
      return produce(state, draft => {
        draft.recent = {
          category,
          sort,
          end: false,
          products: action.payload.data,
          prefetched: null,
        };
      });
    },
  },
  {
    type: PREFETCH_RECENT_PRODUCTS,
    onSuccess: (state: Display, action: ProductsResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        draft.recent.prefetched = data;
        if (data && data.length === 0) {
          draft.recent.end = true;
        }
      });
    },
  },
  {
    type: GET_TAG_PRODUCTS,
    onSuccess: (state: Display, action: ProductsResponseAction) => {
      return produce(state, draft => {
        draft.tag = {
          end: action.payload.data.length < 20,
          products: action.payload.data,
          prefetched: null,
          currentTag: action.meta.tag,
        };
      });
    },
  },
  {
    type: PREFETCH_TAG_PRODUCTS,
    onSuccess: (state: Display, action: ProductsResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        draft.tag.prefetched = data;
        if (data && data.length === 0) {
          draft.tag.end = true;
        }
      });
    },
  },
  {
    type: GET_BADGE_PRODUCTS,
    onSuccess: (state: Display, action: ProductsResponseAction) => {
      return produce(state, draft => {
        draft.badge = {
          end: action.payload.data.length < 20,
          products: action.payload.data,
          prefetched: null,
          currentBadge: action.meta.badge,
        };
      });
    },
  },
  {
    type: PREFETCH_BADGE_PRODUCTS,
    onSuccess: (state: Display, action: ProductsResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        draft.badge.prefetched = data;
        if (data && data.length === 0) {
          draft.badge.end = true;
        }
      });
    },
  },
  {
    type: GET_CATEGORY_PRODUCTS,
    onSuccess: (state: Display, action: ProductsResponseAction) => {
      return produce(state, draft => {
        draft.category = {
          end: action.payload.data.length < 20,
          products: action.payload.data,
          prefetched: null,
          currentCategory: action.meta.category,
          sort: action.meta.sort,
        };
      });
    },
  },
  {
    type: PREFETCH_CATEGORY_PRODUCTS,
    onSuccess: (state: Display, action: ProductsResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        draft.category.prefetched = data;
        if (data && data.length === 0) {
          draft.category.end = true;
        }
      });
    },
  },
  {
    type: GET_TRENDING_PRODUCTS,
    onSuccess: (state: Display, action: ProductsResponseAction) => {
      return produce(state, draft => {
        action.payload.data.forEach(product => {
          draft.trendingMap[product.id] = true;
        });
        draft.trending = {
          end: false,
          products: action.payload.data,
          prefetched: null,
        };
      });
    },
  },
  {
    type: PREFETCH_TRENDING_PRODUCTS,
    onSuccess: (state: Display, action: ProductsResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        const filtered = data.filter(product => !state.trendingMap[product.id]);
        draft.trending.prefetched = filtered;
        filtered.forEach(product => {
          draft.trendingMap[product.id] = true;
        });
        if (data && data.length === 0) {
          draft.trending.end = true;
        }
      });
    },
  },
  {
    type: GET_USER_PRODUCTS,
    onPending: (state: Display) => {
      return produce(state, draft => {
        draft.user.end = false;
        draft.user.prefetched = null;
      });
    },
    onSuccess: (state: Display, action: ProductsResponseAction) => {
      return produce(state, draft => {
        draft.user.products = action.payload.data;
        draft.user.currentUsername = action.meta.username;
        if (action.meta.tag) {
          draft.user.currentTag = action.meta.tag;
        }
        if (action.meta.badge) {
          draft.user.currentBadge = action.meta.badge;
        }
        if (action.payload.data.length < 20) {
          draft.user.end = true;
        }
      });
    },
  },
  {
    type: PREFETCH_USER_PRODUCTS,
    onSuccess: (state: Display, action: ProductsResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        draft.user.prefetched = data;
        if (data && data.length === 0) {
          draft.user.end = true;
        }
      });
    },
  },
  {
    type: GET_TEMP_PRODUCTS,
    onPending: (state: Display) => {
      return produce(state, draft => {
        draft.temp.end = false;
        draft.temp.prefetched = null;
      });
    },
    onSuccess: (state: Display, action: ProductsResponseAction) => {
      return produce(state, draft => {
        draft.temp.products = action.payload.data;
        if (action.payload.data.length < 20) {
          draft.temp.end = true;
        }
      });
    },
  },
  {
    type: PREFETCH_TEMP_PRODUCTS,
    onSuccess: (state: Display, action: ProductsResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        draft.temp.prefetched = data;
        if (data && data.length === 0) {
          draft.temp.end = true;
        }
      });
    },
  },
]);



// WEBPACK FOOTER //
// ./src/store/modules/display.js